import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { PRICING_HERO, PRICING_TRUST_STRIP, PRICING_FAQ, TRUSTED_PAYMENT } from '../../../data/pricingCatalog';
import planService from '../../../services/planService';
import {
  classifyPlan,
  formatPlanPriceDisplay,
  getPlanDisplayCopy,
} from '../../../utils/planCatalogUtils';

/** CTA kind from DB plan_type / name only — no marketing copy overrides. */
function ctaKindForPlan(plan) {
  const { category } = classifyPlan(plan);
  if (category === 'starter') return 'download';
  if (category === 'enterprise') return 'contact';
  return 'upgrade';
}

export default function PricingPage({ onOpenContact, onOpenLogin, onOpenDownload }) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(0);
  const [error, setError] = useState('');
  // ***** Code Start BH10092026_1415 - Monthly / Yearly toggle (match Dashboard SubscriptionPlans)
  const [billingInterval, setBillingInterval] = useState('monthly');
  // ***** Code End BH10092026_1415

  // ***** Code Start BH10092026_1716 - Shared TTL catalog (no Date.now cache-bust)
  useEffect(() => {
    const fetchPlans = async () => {
      setError('');
      try {
        const list = await planService.getSortedPlansList();
        // Active non-addon rows only — name/price/features/description from DB
        setPlans(list.filter((p) => classifyPlan(p).category !== 'addon'));
        if (!list.length) {
          setError('Plans catalog unavailable.');
        }
      } catch (err) {
        console.warn('Failed to fetch pricing plans:', err);
        setPlans([]);
        setError('Failed to load plans from server.');
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);
  // ***** Code End BH10092026_1716
  const handleCta = (plan) => {
    const kind = ctaKindForPlan(plan);
    if (kind === 'contact') {
      onOpenContact?.();
      return;
    }
    if (kind === 'download') {
      if (onOpenDownload) onOpenDownload();
      else if (isAuthenticated) navigate('/dashboard');
      else onOpenLogin?.();
      return;
    }
    // Carry billing interval into signed-in upgrade so Yearly selection is not lost.
    if (isAuthenticated) {
      navigate(`/upgrade?billing_cycle=${encodeURIComponent(billingInterval)}`);
    } else {
      onOpenLogin?.();
    }
  };

  const ctaLabel = (plan) => {
    const kind = ctaKindForPlan(plan);
    if (kind === 'contact') return 'Contact sales';
    if (kind === 'download') return 'Get started';
    return 'Subscribe';
  };

  const visiblePlans = plans.filter((plan) => {
    const { category } = classifyPlan(plan);
    // Pay-as-you-go is monthly-only (same rule as Dashboard PricingPlansGrid).
    if (billingInterval === 'yearly' && category === 'payg') return false;
    return true;
  });

  return (
    <div className="bg-slate-50 min-h-screen pt-8 pb-16 animate-fade-in font-sans">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-10">
        <span className="text-xs font-bold uppercase tracking-wider text-blue-600">
          {PRICING_HERO.kicker}
        </span>
        <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight leading-none mt-2 mb-4">
          {PRICING_HERO.headline}
        </h1>
        <p className="max-w-3xl mx-auto text-slate-600 text-sm sm:text-base mb-3">{PRICING_HERO.lead}</p>
        <p className="max-w-2xl mx-auto text-slate-400 text-xs mb-6">
          <strong>All prices are in USD.</strong> Plan names, prices, limits, and features are loaded from the live database catalog.
        </p>

        {/* ***** Code Start BH10092026_1415 - Website pricing Monthly / Yearly (parity with Dashboard) */}
        <div className="candor-pricing-billing-wrap flex justify-center">
          <div className="candor-segment" role="tablist" aria-label="Billing interval">
            <button
              type="button"
              role="tab"
              aria-selected={billingInterval === 'monthly'}
              className={`candor-segment-btn ${billingInterval === 'monthly' ? 'is-active' : ''}`}
              onClick={() => setBillingInterval('monthly')}
            >
              Monthly
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={billingInterval === 'yearly'}
              className={`candor-segment-btn candor-segment-btn--yearly ${billingInterval === 'yearly' ? 'is-active' : ''}`}
              onClick={() => setBillingInterval('yearly')}
            >
              Yearly
              <span className="candor-segment-discount" aria-label="20 percent off yearly billing">
                {PRICING_HERO.yearlyDiscountBadge}
              </span>
            </button>
          </div>
        </div>
        <ul className="candor-trust-row mt-5 justify-center" aria-label="Pricing guarantees">
          {PRICING_HERO.trustChips.map((chip) => (
            <li key={chip.id}>
              <span className="candor-trust-chip">{chip.label}</span>
            </li>
          ))}
        </ul>
        {/* ***** Code End BH10092026_1415 */}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin inline-block w-8 h-8 border-4 border-slate-200 border-t-blue-600 rounded-full" />
            <p className="text-slate-500 text-sm mt-3">Loading plans…</p>
          </div>
        ) : error || visiblePlans.length === 0 ? (
          <p className="text-center text-slate-500 text-sm py-16">
            {error || 'No active plans returned from the database.'} Contact sales@candorsys.com.
          </p>
        ) : (
          <div className="candor-pricing-grid">
            {visiblePlans.map((plan) => {
              const name = String(plan.plan_name || plan.name || '').trim() || 'Plan';
              const description = String(plan.description || '').trim();
              const features = Array.isArray(plan.features) ? plan.features : [];
              const intervalForPrice =
                billingInterval === 'yearly' && classifyPlan(plan).category === 'payg'
                  ? 'monthly'
                  : billingInterval;
              const p = formatPlanPriceDisplay(plan, intervalForPrice);
              const copy = getPlanDisplayCopy(plan, intervalForPrice);
              const { category } = classifyPlan(plan);
              const highlight = category === 'developer';
              return (
                <article
                  key={plan.db_id || plan.id || name}
                  className={`candor-plan-card relative hover:-translate-y-1 transition-all ${
                    highlight ? 'ring-2 ring-blue-500/80 border-blue-500/50 lg:scale-105' : ''
                  }`}
                >
                  <div className="candor-plan-card__body">
                    <h3 className="candor-plan-card__title">{name}</h3>
                    {description ? <p className="candor-plan-card__tagline">{description}</p> : null}
                    <div className="candor-plan-card__price-row">
                      <span className="candor-plan-card__price">{p.amount}</span>
                      {p.suffix ? <span className="candor-plan-card__price-suffix">{p.suffix}</span> : null}
                    </div>
                    {copy.priceNote && billingInterval === 'yearly' && category === 'developer' ? (
                      <p className="candor-plan-card__desc text-xs text-slate-500">{copy.priceNote}</p>
                    ) : null}
                    {category !== 'enterprise' &&
                    plan.monthly_limit != null &&
                    Number(plan.monthly_limit) > 0 &&
                    Number(plan.monthly_limit) < 100000000 ? (
                      <p className="candor-plan-card__desc text-xs text-slate-500">
                        Monthly token limit: {Number(plan.monthly_limit).toLocaleString()}
                      </p>
                    ) : null}
                    <hr className="candor-plan-card__divider" />
                    <h4 className="candor-plan-card__includes">Includes:</h4>
                    <ul className="candor-plan-features">
                      {features.length === 0 ? (
                        <li>
                          <span>—</span>
                          <span>No features listed in catalog</span>
                        </li>
                      ) : (
                        features.map((f, i) => (
                          <li key={i}>
                            <span className={highlight ? 'text-blue-500 font-bold' : ''}>✓</span>
                            <span>{f}</span>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                  <div className="candor-plan-card__footer">
                    <button
                      type="button"
                      onClick={() => handleCta(plan)}
                      className={`w-full py-2.5 font-bold rounded-xl text-xs transition duration-200 shadow-md ${
                        highlight
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : category === 'enterprise'
                            ? 'bg-slate-800 hover:bg-slate-900 text-white'
                            : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200'
                      }`}
                    >
                      {ctaLabel(plan)}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <h2 className="text-xl font-bold text-slate-900 text-center mb-6">{PRICING_TRUST_STRIP.title}</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {PRICING_TRUST_STRIP.items.map((item) => (
            <div key={item.title} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
              <h3 className="text-sm font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-xs text-slate-600 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <h2 className="text-xl font-bold text-slate-900 text-center mb-6">Frequently Asked Questions</h2>
        <div className="space-y-3">
          {PRICING_FAQ.map((item, idx) => (
            <div key={item.q} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <button
                type="button"
                className="w-full text-left px-4 py-3 text-sm font-semibold text-slate-900 flex justify-between gap-3"
                onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
              >
                <span>{item.q}</span>
                <span className="text-slate-400">{openFaq === idx ? '−' : '+'}</span>
              </button>
              {openFaq === idx && (
                <p className="px-4 pb-4 text-xs text-slate-600 leading-relaxed">{item.a}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-12 space-y-4">
        <div className="flex bg-slate-100 border border-slate-200 p-4 rounded-xl gap-3 text-xs text-slate-600">
          <span>🧾</span>
          <p>{PRICING_HERO.taxNote}</p>
        </div>
        <div className="bg-slate-900 text-white rounded-3xl p-6 sm:p-10 border border-slate-800 shadow-xl flex flex-col md:flex-row gap-6 justify-between items-center mt-8">
          <div className="space-y-3">
            <h2 className="text-xl font-bold">{TRUSTED_PAYMENT.title}</h2>
            <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">{TRUSTED_PAYMENT.body}</p>
          </div>
          <aside className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center md:w-80 w-full shrink-0">
            <h4 className="font-bold text-slate-200 mb-2">{TRUSTED_PAYMENT.needHelpTitle}</h4>
            <a href="mailto:sales@candorsys.com" className="text-blue-400 font-semibold text-sm hover:underline block">
              sales@candorsys.com
            </a>
          </aside>
        </div>
      </div>
    </div>
  );
}
